import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMobileAuth } from '../../context/MobileAuthContext';
import { useMobileChat } from '../../context/MobileChatContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { 
  Briefcase, 
  Clock, 
  FileText, 
  LogOut, 
  ChevronRight, 
  Calendar,
  Loader2,
  MapPin,
  Truck,
  Bell,
  MessageSquare,
  ClipboardList,
  HelpCircle,
  Lock,
  Shield,
  FileCheck,
  KeyRound,
  Settings,
  Sparkles
} from 'lucide-react';
import AddToHomeScreen from '../../components/mobile/AddToHomeScreen';

const MobileDashboard = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { operator, api, logout, hasFeature, hasMobileQuotesAccess, deviceToken, pendingPinSetup } = useMobileAuth();
  const { unreadCount: chatUnreadCount, fetchRooms } = useMobileChat();
  const [todayJobs, setTodayJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showPinBanner, setShowPinBanner] = useState(true);

  useEffect(() => {
    fetchTodayJobs();
    fetchNotificationCount();
    fetchRooms(); // Fetch chat rooms to get unread count
    
    // Poll for new notifications every 30 seconds
    const interval = setInterval(() => {
      fetchNotificationCount();
      fetchRooms();
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchTodayJobs = async () => {
    try {
      // Get user's LOCAL date (not UTC) for accurate timezone handling
      const now = new Date();
      const userToday = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
      const response = await api.get(`/api/mobile/jobs?date_filter=today&user_today=${userToday}`);
      setTodayJobs(response.data);
    } catch (error) {
      console.error('Failed to fetch jobs');
    } finally {
      setLoading(false);
    }
  };

  const fetchNotificationCount = async () => {
    try {
      const response = await api.get('/api/mobile/notifications/count');
      setUnreadCount(response.data.unread_count);
    } catch (error) {
      console.error('Failed to fetch notification count');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/mobile/login');
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return '';
    return timeStr.slice(0, 5);
  };

  return (
    <div className="min-h-screen bg-[#1a1a1a]">
      {/* Header */}
      <div className="bg-[#2a2a2a] px-4 py-4 sticky top-0 z-10">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-gray-400 text-xs">{t('mobile.dashboard.welcome')}</p>
            <h1 className="text-white font-bold text-lg">{operator?.name}</h1>
          </div>
          <div className="flex items-center gap-2">
            {/* Settings Button */}
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => navigate('/mobile/settings')} 
              className="text-gray-400 hover:text-white"
              data-testid="dashboard-settings-button"
            >
              <Settings className="h-5 w-5" />
            </Button>
            {/* Notification Bell */}
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => navigate('/mobile/notifications')} 
              className="text-gray-400 relative"
            >
              <Bell className="h-5 w-5" />
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] bg-[#ffd108] text-[#1a1a1a] text-xs font-bold rounded-full flex items-center justify-center px-1">
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              )}
            </Button>
            <Button variant="ghost" size="sm" onClick={handleLogout} className="text-gray-400">
              <LogOut className="h-5 w-5" />
            </Button>
          </div>
        </div>
      </div>

      <div className="p-4 space-y-4">
        {/* PIN Setup Banner - Show when PIN is not set */}
        {!deviceToken && showPinBanner && (
          <Card className="bg-gradient-to-r from-[#ffd108]/20 to-[#ffd108]/5 border-[#ffd108]/30" data-testid="pin-setup-banner">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-full bg-[#ffd108]/20 flex items-center justify-center flex-shrink-0">
                  <Sparkles className="h-5 w-5 text-[#ffd108]" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-white font-medium text-sm">Enable Quick Login</h3>
                  <p className="text-gray-400 text-xs mt-0.5">Set up a PIN for faster access next time</p>
                  <div className="flex gap-2 mt-3">
                    <Button 
                      size="sm"
                      onClick={() => navigate('/mobile/settings')}
                      className="btn-volt text-xs h-8"
                      data-testid="banner-setup-pin-button"
                    >
                      <KeyRound className="h-3.5 w-3.5 mr-1.5" />
                      Set Up PIN
                    </Button>
                    <Button 
                      size="sm"
                      variant="ghost"
                      onClick={() => setShowPinBanner(false)}
                      className="text-gray-400 hover:text-white text-xs h-8"
                      data-testid="banner-dismiss-button"
                    >
                      Later
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Quick Actions - Row 1 */}
        <div className="grid grid-cols-4 gap-3">
          <Button
            variant="outline"
            className="h-auto py-4 flex flex-col items-center gap-2 bg-[#2a2a2a] border-gray-700 text-white hover:bg-[#ffd108] hover:text-[#1a1a1a] hover:border-[#ffd108]"
            onClick={() => navigate('/mobile/jobs')}
          >
            <Briefcase className="h-6 w-6" />
            <span className="text-xs">{t('mobile.menu.jobs')}</span>
          </Button>
          {hasFeature('timesheets') ? (
            <Button
              variant="outline"
              className="h-auto py-4 flex flex-col items-center gap-2 bg-[#2a2a2a] border-gray-700 text-white hover:bg-[#ffd108] hover:text-[#1a1a1a] hover:border-[#ffd108]"
              onClick={() => navigate('/mobile/timesheet')}
            >
              <Clock className="h-6 w-6" />
              <span className="text-xs">{t('mobile.menu.timesheet')}</span>
            </Button>
          ) : (
            <Button
              variant="outline"
              className="h-auto py-4 flex flex-col items-center gap-2 bg-[#2a2a2a] border-gray-700 text-gray-500 cursor-not-allowed opacity-60"
              disabled
            >
              <div className="relative">
                <Clock className="h-6 w-6" />
                <Lock className="h-3 w-3 absolute -bottom-1 -right-1 text-gray-400" />
              </div>
              <span className="text-xs">{t('mobile.menu.timesheet')}</span>
            </Button>
          )}
          {hasFeature('forms') ? (
            <Button
              variant="outline"
              className="h-auto py-4 flex flex-col items-center gap-2 bg-[#2a2a2a] border-gray-700 text-white hover:bg-[#ffd108] hover:text-[#1a1a1a] hover:border-[#ffd108]"
              onClick={() => navigate('/mobile/forms')}
            >
              <ClipboardList className="h-6 w-6" />
              <span className="text-xs">Forms</span>
            </Button>
          ) : (
            <Button
              variant="outline"
              className="h-auto py-4 flex flex-col items-center gap-2 bg-[#2a2a2a] border-gray-700 text-gray-500 cursor-not-allowed opacity-60"
              disabled
            >
              <div className="relative">
                <ClipboardList className="h-6 w-6" />
                <Lock className="h-3 w-3 absolute -bottom-1 -right-1 text-gray-400" />
              </div>
              <span className="text-xs">Forms</span>
            </Button>
          )}
          {hasFeature('messaging') ? (
            <Button
              variant="outline"
              className="h-auto py-4 flex flex-col items-center gap-2 bg-[#2a2a2a] border-gray-700 text-white hover:bg-[#ffd108] hover:text-[#1a1a1a] hover:border-[#ffd108] relative"
              onClick={() => navigate('/mobile/chat')}
            >
              <MessageSquare className="h-6 w-6" />
              <span className="text-xs">Chat</span>
              {chatUnreadCount > 0 && (
                <span className="absolute top-2 right-3 min-w-[18px] h-[18px] bg-[#ffd108] text-[#1a1a1a] text-xs font-bold rounded-full flex items-center justify-center px-1">
                  {chatUnreadCount > 99 ? '99+' : chatUnreadCount}
                </span>
              )}
            </Button>
          ) : (
            <Button
              variant="outline"
              className="h-auto py-4 flex flex-col items-center gap-2 bg-[#2a2a2a] border-gray-700 text-gray-500 cursor-not-allowed opacity-60"
              disabled
            >
              <div className="relative">
                <MessageSquare className="h-6 w-6" />
                <Lock className="h-3 w-3 absolute -bottom-1 -right-1 text-gray-400" />
              </div>
              <span className="text-xs">Chat</span>
            </Button>
          )}
        </div>
        
        {/* Quick Actions - Row 2 */}
        <div className="grid grid-cols-4 gap-3">
          <Button
            variant="outline"
            className="h-auto py-4 flex flex-col items-center gap-2 bg-[#2a2a2a] border-gray-700 text-white hover:bg-[#ffd108] hover:text-[#1a1a1a] hover:border-[#ffd108] relative"
            onClick={() => navigate('/mobile/notifications')}
          >
            <Bell className="h-6 w-6" />
            <span className="text-xs">{t('mobile.menu.notifications')}</span>
            {unreadCount > 0 && (
              <span className="absolute top-2 right-3 min-w-[18px] h-[18px] bg-[#ffd108] text-[#1a1a1a] text-xs font-bold rounded-full flex items-center justify-center px-1">
                {unreadCount > 99 ? '99+' : unreadCount}
              </span>
            )}
          </Button>
          {hasFeature('compliance') ? (
            <Button
              variant="outline"
              className="h-auto py-4 flex flex-col items-center gap-2 bg-[#2a2a2a] border-gray-700 text-white hover:bg-[#ffd108] hover:text-[#1a1a1a] hover:border-[#ffd108]"
              onClick={() => navigate('/mobile/compliance')}
              data-testid="compliance-btn"
            >
              <Shield className="h-6 w-6" />
              <span className="text-xs">Compliance</span>
            </Button>
          ) : (
            <Button
              variant="outline"
              className="h-auto py-4 flex flex-col items-center gap-2 bg-[#2a2a2a] border-gray-700 text-gray-500 cursor-not-allowed opacity-60"
              disabled
            >
              <div className="relative">
                <Shield className="h-6 w-6" />
                <Lock className="h-3 w-3 absolute -bottom-1 -right-1 text-gray-400" />
              </div>
              <span className="text-xs">Compliance</span>
            </Button>
          )}
          {hasMobileQuotesAccess() ? (
            <Button
              variant="outline"
              className="h-auto py-4 flex flex-col items-center gap-2 bg-[#2a2a2a] border-gray-700 text-white hover:bg-[#ffd108] hover:text-[#1a1a1a] hover:border-[#ffd108]"
              onClick={() => navigate('/mobile/quotes')}
              data-testid="quotes-btn"
            >
              <FileCheck className="h-6 w-6" />
              <span className="text-xs">Quotes</span>
            </Button>
          ) : hasFeature('quotes') ? (
            <Button
              variant="outline"
              className="h-auto py-4 flex flex-col items-center gap-2 bg-[#2a2a2a] border-gray-700 text-gray-500 cursor-not-allowed opacity-60"
              disabled
              title="Quoting access not enabled for your account"
            >
              <div className="relative">
                <FileCheck className="h-6 w-6" />
                <Lock className="h-3 w-3 absolute -bottom-1 -right-1 text-gray-400" />
              </div>
              <span className="text-xs">Quotes</span>
            </Button>
          ) : null}
          <Button
            variant="outline"
            className="h-auto py-4 flex flex-col items-center gap-2 bg-[#2a2a2a] border-gray-700 text-white hover:bg-[#ffd108] hover:text-[#1a1a1a] hover:border-[#ffd108]"
            onClick={() => navigate('/mobile/help')}
          >
            <HelpCircle className="h-6 w-6" />
            <span className="text-xs">Help</span>
          </Button>
        </div>

        {/* Today's Jobs */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-white font-semibold flex items-center gap-2">
              <Calendar className="h-4 w-4 text-[#ffd108]" />
              {t('mobile.dashboard.todaysJobs')}
            </h2>
            <Badge variant="secondary" className="bg-[#ffd108] text-[#1a1a1a]">
              {todayJobs.length}
            </Badge>
          </div>

          {loading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-[#ffd108]" />
            </div>
          ) : todayJobs.length === 0 ? (
            <Card className="bg-[#2a2a2a] border-gray-700">
              <CardContent className="py-8 text-center">
                <Briefcase className="h-12 w-12 mx-auto text-gray-600 mb-3" />
                <p className="text-gray-400">{t('mobile.jobs.noJobs')}</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {todayJobs.map((job) => {
                // Get user's local "today" date for navigation
                const now = new Date();
                const userToday = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
                // Use viewing_date from job if available (for multi-day jobs), otherwise use today
                const navDate = job.viewing_date || userToday;
                
                return (
                  <Card 
                    key={job.id} 
                    className="bg-[#2a2a2a] border-gray-700 cursor-pointer hover:border-[#ffd108] transition-colors"
                    onClick={() => navigate(`/mobile/jobs/${job.id}?date=${navDate}`)}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-[#ffd108] font-bold text-sm">
                              {formatTime(job.start_time)}{job.end_time ? ` - ${formatTime(job.end_time)}` : ''}
                            </span>
                            {job.has_docket && (
                              <Badge variant="secondary" className="bg-green-900 text-green-300 text-xs">
                                Docketed
                              </Badge>
                            )}
                          </div>
                          <h3 className="text-white font-medium">{job.customer_name}</h3>
                          <p className="text-gray-400 text-sm flex items-center gap-1">
                            <MapPin className="h-3 w-3" />
                            {job.site_name}
                          </p>
                          <p className="text-gray-500 text-xs flex items-center gap-1 mt-1">
                            <Truck className="h-3 w-3" />
                            {job.equipment_name}
                          </p>
                        </div>
                        <ChevronRight className="h-5 w-5 text-gray-500" />
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </div>

        {/* View All Jobs Button */}
        <Button 
          variant="outline" 
          className="w-full bg-[#2a2a2a] border-gray-700 text-white hover:bg-[#ffd108] hover:text-[#1a1a1a] hover:border-[#ffd108]"
          onClick={() => navigate('/mobile/jobs')}
        >
          View All Jobs
          <ChevronRight className="h-4 w-4 ml-2" />
        </Button>
      </div>
      
      {/* PWA Add to Home Screen Prompt */}
      <AddToHomeScreen />
    </div>
  );
};

export default MobileDashboard;
